import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Target } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { LeaderPortrait } from "../components/LeaderPortrait";
import { Reveal } from "../components/Reveal";
import { CORE_VALUES, PEOPLE } from "../data/content";

export default function About() {
  return (
    <main data-testid="about-page" className="bg-[#070C14] text-white">
      <PageHero
        testId="about-hero"
        eyebrow="Company profile"
        title="A Nigerian resource partner with a global value web."
        description="Clach leverages a unique business model and top-line global connectivity to deliver best-in-class products, capabilities, and solutions across upstream and downstream oil and gas markets."
        image="/assets/page_3_img_2.jpeg"
        imageAlt="Strategic chess pieces representing Clach's business approach"
      />

      <section data-testid="about-story-section" className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-8 lg:grid-cols-[.85fr_1.15fr] lg:px-10">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">About us</p>
          <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Customized solutions. Responsive execution. Price-value advantage.</h2>
        </Reveal>
        <Reveal delay={0.1} className="space-y-6 text-base leading-8 text-slate-300">
          <p>
            CLACH Global Services Limited is a duly registered company in Nigeria and an innovative resource and solutions provider serving niche sectors of the Nigerian oil and gas market in both upstream and downstream operations.
          </p>
          <p>
            The downstream procurement business covers importation, warehousing, bulk purchase, sales and distribution of refined petroleum products including AGO, DPK, fuel oil and base oil. Upstream services include procurement and supply chain management.
          </p>
          <p>
            The management team brings more than sixty years of combined operating experience across the oil and gas value chain, supported by best-of-breed partner organizations within and outside Nigeria.
          </p>
        </Reveal>
      </section>

      <section data-testid="vision-mission-section" className="mx-auto grid max-w-7xl gap-6 px-4 pb-24 sm:px-8 lg:grid-cols-2 lg:px-10">
        {[
          { icon: Eye, title: "Our Vision", text: "To become Africa's first-choice company in the business of maritime, oil and gas." },
          { icon: Target, title: "Our Mission", text: "To continuously improve every business activity so clients receive comprehensive, high-standard, cost-effective products and performance." },
        ].map((item, index) => (
          <Reveal key={item.title} delay={index * 0.1} className="spotlight rounded-3xl border border-white/10 bg-[#0D131F] p-8 sm:p-10">
            <item.icon className="text-amber-400" size={30} />
            <h3 className="mt-10 font-heading text-3xl font-semibold">{item.title}</h3>
            <p className="mt-5 text-base leading-8 text-slate-300" data-testid={`${item.title.toLowerCase().replace(/\s+/g, "-")}-text`}>{item.text}</p>
          </Reveal>
        ))}
      </section>

      <section data-testid="core-values-section" className="border-y border-white/10 bg-[#0D131F] px-4 py-24 sm:px-8 lg:px-10">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.8fr_1.2fr]">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.32em] text-blue-400">Core values</p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">The internal code behind every external promise.</h2>
            <div className="clip-frame mt-10 h-80 overflow-hidden border border-white/10">
              <img src="/assets/page_6_img_1.jpeg" alt="Apple core symbolizing Clach core values" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div className="grid content-center gap-4 sm:grid-cols-2">
            {CORE_VALUES.map((value, index) => (
              <Reveal key={value} delay={index * 0.06} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <p className="font-mono text-xs text-amber-400">0{index + 1}</p>
                <h3 className="mt-5 font-heading text-xl font-semibold" data-testid={`core-value-${index + 1}`}>{value}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="clach-people-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-10">
        <Reveal className="mb-12 max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-amber-400">Clach people</p>
          <h2 className="mt-4 font-heading text-3xl font-bold sm:text-5xl">Leadership across finance, operations, HSE, HR, legal, and logistics.</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PEOPLE.map((person, index) => (
            <Reveal key={person.slug} delay={index * 0.05}>
              <Link to={`/leadership/${person.slug}`} data-testid={`leader-card-${person.slug}`} className="group block h-full rounded-3xl border border-white/10 bg-[#0D131F] p-7 transition-all duration-500 hover:-translate-y-2 hover:border-amber-400/40">
                <LeaderPortrait person={person} />
                <div className="mt-8 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-2xl font-semibold" data-testid={`person-${person.slug}`}>{person.name}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-400">{person.role}</p>
                  </div>
                  <ArrowUpRight className="mt-1 shrink-0 text-blue-400 transition-all duration-300 group-hover:rotate-45 group-hover:text-amber-400" size={22} />
                </div>
                <p className="mt-6 text-xs uppercase tracking-[0.18em] text-slate-500">{person.focusAreas.slice(0, 2).join(" · ")}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
